import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';

const ClaimAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Извлекаем токен из URL при загрузке
  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('Отсутствует токен привязки. Вернитесь в Telegram бот и попробуйте снова.');
    } else {
      setLinkToken(token);
    }
  }, [searchParams]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    // Валидация
    if (password.length < 6) {
      setError('Пароль должен содержать минимум 6 символов');
      return;
    }

    if (password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (!linkToken) {
      setError('Токен привязки отсутствует');
      return;
    }

    setIsLoading(true);

    try {
      // Вызываем API для привязки аккаунта
      const response = await fetch('/api/v1/web-auth/claim-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          link_token: linkToken,
          password: password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `HTTP Error ${response.status}` }));
        throw new Error(errorData.detail || 'Ошибка привязки аккаунта');
      }

      const data = await response.json();

      // Сохраняем токен
      localStorage.setItem('authToken', data.access_token);

      // Перенаправляем на login
      window.location.href = '/login';
    } catch (err: any) {
      setError(err.message || 'Ошибка привязки аккаунта. Попробуйте получить новую ссылку в боте.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        py={4}
      >
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
              🔗 Привязка Telegram аккаунта
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" paragraph>
              Создайте пароль для доступа к вашему аккаунту через веб-интерфейс
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {!linkToken && !error && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Загрузка токена привязки...
                </Alert>
              )}

              {linkToken && (
                <>
                  <TextField
                    fullWidth
                    label="Пароль"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    inputProps={{ minLength: 6 }}
                    autoComplete="new-password"
                    margin="normal"
                    helperText="Минимум 6 символов"
                  />

                  <TextField
                    fullWidth
                    label="Подтвердите пароль"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    inputProps={{ minLength: 6 }}
                    autoComplete="new-password"
                    margin="normal"
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isLoading || !linkToken}
                    sx={{ mt: 3, mb: 2 }}
                  >
                    {isLoading ? 'Привязка аккаунта...' : '✅ Привязать аккаунт'}
                  </Button>
                </>
              )}

              <Box textAlign="center" mt={2}>
                <Typography variant="body2" color="text.secondary">
                  Уже есть аккаунт?{' '}
                  <MuiLink component={Link} to="/login">
                    Войти
                  </MuiLink>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Токен привязки действителен бессрочно
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ClaimAccountPage;
